import {
	FREE_PLAN_NUM_NODES_LIMIT,
	MODEL_BASE_NODE_DEF_TYPE,
	ModelArchitecture,
	TEAM_BOARD_NUM_NODES_LIMIT,
} from '@run-diffusion/shared';

export const SELECTED_IMAGE_MODE: {
	TILED: 'TILED'
	INFO: 'INFO'
} = {
	TILED: 'TILED',
	INFO: 'INFO',
};

export type NodeRunResultSelectionMode =
	'SINGLE' |
	'MULTIPLE';

export const NODE_RUN_RESULT_SELECTION_MODE: Record<NodeRunResultSelectionMode, NodeRunResultSelectionMode> = {
	SINGLE: 'SINGLE',
	MULTIPLE: 'MULTIPLE',
};

export const RUNNIT_BULK_ACTIONS_TRIGGER_ID: string = 'RUNNIT_BULK_ACTIONS';

interface SettingAction {
	id: string
	label: string
	icon: string
}

export const SETTING_ACTIONS: {
	COPY: SettingAction
} = {
	COPY: {
		id: 'COPY',
		label: 'Copy',
		icon: 'mdi-clipboard-text-outline',
	},
};
export const RUNNITS_OWNER_SELECTION: {
	USER: 'USER'
	TEAM: 'TEAM'
} = {
	USER: 'USER',
	TEAM: 'TEAM',
};
export const RUNNIT_TOOL_SETTINGS_VALUES: {
	VIEW_INFO: string
	DELETE: string
	MOVE_UP: string
	MOVE_DOWN: string
	EDIT_INFO: string
} = {
	VIEW_INFO: 'VIEW_INFO',
	DELETE: 'DELETE',
	MOVE_UP: 'MOVE_UP',
	MOVE_DOWN: 'MOVE_DOWN',
	EDIT_INFO: 'EDIT_INFO',
};
export const RUNNIT_TOOL_SETTINGS_MENU_ITEMS: {
	id: number
	icon: string
	label: string
	value: string
	needsCanEditRunnitPermission: boolean
}[] = [
		{
			id: 1,
			icon: 'mdi-information-outline',
			label: 'View Tool Description',
			value: RUNNIT_TOOL_SETTINGS_VALUES.VIEW_INFO,
			needsCanEditRunnitPermission: false,
		},
		{
			id: 2,
			icon: 'mdi-pencil-outline',
			label: 'Edit Name, etc.',
			value: RUNNIT_TOOL_SETTINGS_VALUES.EDIT_INFO,
			needsCanEditRunnitPermission: true,
		},
		{
			id: 3,
			icon: 'mdi-delete-outline',
			label: 'Delete',
			value: RUNNIT_TOOL_SETTINGS_VALUES.DELETE,
			needsCanEditRunnitPermission: true,
		},
	];
export const SELECTED_IMAGE_MODE_TILED_LABEL: string = 'Tiled (Columns) View';
export const SELECTED_IMAGE_MODE_INFO_LABEL: string = 'Info (Detailed) View';
export const SELECTED_IMAGE_MODE_MENU_ITEMS: {
	id: number
	icon: string
	label: string
	value: string
}[] = [
		{
			id: 1,
			icon: 'mdi-table',
			label: SELECTED_IMAGE_MODE_TILED_LABEL,
			value: SELECTED_IMAGE_MODE.TILED,
		},
		{
			id: 2,
			icon: 'mdi-table-of-contents',
			label: SELECTED_IMAGE_MODE_INFO_LABEL,
			value: SELECTED_IMAGE_MODE.INFO,
		},
	];
export const SELECTED_COLUMNS_MAP = {
	[12]: '1',
	[6]: '2',
	[4]: '3',
	[3]: '4',
	[2]: '6',
	[1]: '12',
};
export const SELECTED_COLUMN_MENU_ITEMS: {
	id: number
	icon: string
	iconStyle?: Record<string, string>
	label: string
	value: number
}[] = [
		{
			id: 1,
			icon: 'mdi-format-columns',
			label: '1 Column',
			value: 12,
		},
		{
			id: 2,
			icon: 'mdi-format-columns',
			label: '2 Columns',
			value: 6,
		},
		{
			id: 3,
			icon: 'mdi-format-columns',
			label: '3 Columns',
			value: 4,
		},
		{
			id: 4,
			icon: 'mdi-format-columns',
			label: '4 Columns',
			value: 3,
		},
		{
			id: 5,
			icon: 'mdi-format-columns',
			label: '6 Columns',
			value: 2,
		},
		{
			id: 6,
			icon: 'mdi-format-columns',
			label: '12 Columns',
			value: 1,
		},
	];

export const SELECTED_PROMPT_VIEW: {
	PROMPT_DRIVEN: 'PROMPT_DRIVEN'
	DRAWER_FORM: 'DRAWER_FORM'
} = {
	PROMPT_DRIVEN: 'PROMPT_DRIVEN',
	DRAWER_FORM: 'DRAWER_FORM',
};

// Kit plumbing (query param + enum). The Kit Type union itself now lives in
// the deps-free shared/kits leaf, since it is the runnitKits document id that
// every surface reads; re-exported here so existing web imports still work.
export const KIT_QUERY_PARAM: string = 'kit';
export { KIT_TYPE } from '@run-diffusion/shared';
export type { KitType } from '@run-diffusion/shared';

export const RUNNIT_APEX_RUNNIT_NODE_DEF_ID: string = 'mzttaygaC3NXy6qOerIn';
export const FEATURED_RUNNIT_NODE_DEF_IDS: string[] = [
	RUNNIT_APEX_RUNNIT_NODE_DEF_ID
];

export const FREE_PLAN_LIMIT_CLUB_ADD_ON_NUM_RUNNITS_LIMIT: number = 3; // Free plan limit value

// From shared, under the name the app already uses. The server refuses the add
// at this number, so a value that drifts here would show an unreachable limit.
export const FREE_PLAN_LIMIT_CLUB_ADD_ON_NUM_NODES_LIMIT: number = FREE_PLAN_NUM_NODES_LIMIT;

// Team boards, pending team Runnit subscriptions. Was hardcoded at each use.
export const TEAM_BOARD_LIMIT_NUM_NODES_LIMIT: number = TEAM_BOARD_NUM_NODES_LIMIT;

export type PortalLocation =
	'TRAINER_IMAGE_UPLOAD'
	;
export const PORTAL_LOCATION: Record<PortalLocation, PortalLocation> = {
	TRAINER_IMAGE_UPLOAD: 'TRAINER_IMAGE_UPLOAD',
}

const lightningQualityOptionCard = {
	title: 'Lightning',
	icon: 'mdi-lightning-bolt',
	description: 'Works great for most use-cases!',
	timeEstimate: '1-3 minutes',
}
const standardQualityOptionCard = {
	title: 'Standard',
	icon: 'mdi-creation',
	description: 'Produces higher-quality results',
	timeEstimate: '20-25 minutes',
}

const humanTypeOptionCard = {
	title: 'Human',
	icon: 'mdi-face-woman',
	description: 'Generate realistic humanlike results. Perfect for portraits, fashion, or lifelike individuals.',
	imagePaths: ['greensuitman1.jpg', 'greensuitman2.jpg', 'greensuitman3.jpg', 'greensuitman4.jpg'],
}

const characterTypeOptionCard = {
	title: 'Character',
	icon: 'mdi-account',
	description: 'Best for stylized creations like cartoons, illustrations, mascots, and non-photorealistic characters with personality.',
	imagePaths: ['popeye1.jpg', 'popeye2.jpg', 'popeye3.jpg', 'popeye4.jpg'],
}

const styleTypeOptionCard = {
	title: 'Art Style',
	icon: 'mdi-brush',
	description: 'Recreate the magic of your favorite artists or explore new aesthetics. Train a model to generate images in your unique style.',
	imagePaths: ['undraw1.jpg', 'undraw2.jpg', 'undraw3.jpg', 'undraw4.jpg'],
}

const objectTypeOptionCard = {
	title: 'Object',
	icon: 'mdi-cube',
	description: 'Need inspiration for your next project? Generate images of specific objects, from everyday items to fantastical creations.',
	imagePaths: ['ander1.jpg', 'ander2.jpg', 'ander3.jpg', 'ander4.jpg'],
}

const petTypeOptionCard = {
	title: 'Pet',
	icon: 'mdi-paw',
	description: 'Turn your furry (or feathery) friend into AI magic! Generate lifelike portraits of animals with all their charm.',
	imagePaths: ['petdog1.jpg', 'petdog2.jpg', 'petdog3.jpg', 'petdog4.jpg'],
}

export const TRAINING_TYPE_CARDS: Record<string, any> = {
	'5Gn92Gevrl1RL4oE24pS': humanTypeOptionCard,
	'MmXNBoWAcBdwRNV2EDGq': characterTypeOptionCard,
	'cJOrWSRNYwt6szmO8MF5': styleTypeOptionCard,
	'eb0OsnA83NYtQO7lVuhH': objectTypeOptionCard,
	'nlhmoOmTgX4FKXZgVqMM': petTypeOptionCard,
}

export const TRAINING_QUALITY_CARDS: Record<string, any> = {
	'aPmZFBQ4b0Tq6o0w2m0X': lightningQualityOptionCard,
	'YgT3v5tSt7FJbPmqsZ9o': standardQualityOptionCard,
}

const fluxBaseUseOptionCard = {
	title: 'Image Generation',
	icon: 'mdi-cube',
	description: 'Start generating images right away',
	buttonText: 'Use in tool',
}

const fluxFillUseOptionCard = {
	title: 'Inpainting (Fill)',
	icon: 'mdi-cube',
	description: 'Allows you to draw on an image to then generate and fill in with a prompt',
	buttonText: 'Use in tool',
}

const sdxlBaseUseOptionCard = {
	title: 'Image Generation',
	icon: 'mdi-cube',
	description: 'Start generating images right away',
	buttonText: 'Use in tool',
}

const fluxBaseCreateOptionCard = {
	title: 'Image Generation',
	icon: 'mdi-cube',
	description: 'Start generating images right away',
	buttonText: 'Create tool',
}

const fluxFillCreateOptionCard = {
	title: 'Inpainting (Fill)',
	icon: 'mdi-cube',
	description: 'Allows you to draw on an image to then generate and fill in with a prompt',
	buttonText: 'Create tool',
}

const sdxlBaseCreateOptionCard = {
	title: 'Image Generation',
	icon: 'mdi-cube',
	description: 'Start generating images right away',
	buttonText: 'Create tool',
}

export const MODEL_BASE_NODE_DEF_TYPE_CARD: Record<string, any> = {
	FLUX_BASE_CREATE: fluxBaseCreateOptionCard,
	FLUX_FILL_CREATE: fluxFillCreateOptionCard,
	SDXL_BASE_CREATE: sdxlBaseCreateOptionCard,
	FLUX_BASE_USE: fluxBaseUseOptionCard,
	FLUX_FILL_USE: fluxFillUseOptionCard,
	SDXL_BASE_USE: sdxlBaseUseOptionCard,
}

export const MODEL_ARCHITECTURE_NODE_DEF_ID_MAP: Record<string, string> = {
	FLUX_BASE_CREATE: MODEL_BASE_NODE_DEF_TYPE.FLUX_BASE,
	FLUX_FILL_CREATE: MODEL_BASE_NODE_DEF_TYPE.FLUX_FILL,
	SDXL_BASE_CREATE: MODEL_BASE_NODE_DEF_TYPE.SDXL_BASE,
	FLUX_BASE_USE: 'PeRStxL3PWOdVeizQGoF',
	FLUX_FILL_USE: 'F7I9ZjiNnsqtRFv74VsJ',
	SDXL_BASE_USE: 'nHQ6FRVvVxsF3ajAUsj1',
};

export const BASE_NODE_DEF_TO_USE_MODEL_WITH: Record<ModelArchitecture, string[]> = {
	FLUX: [
		'FLUX_BASE_USE',
		'FLUX_FILL_USE'
	],
	SDXL: [
		'SDXL_BASE_USE',
	],
}

export const BASE_NODE_DEF_TO_CREATE_MODEL_FROM: Record<ModelArchitecture, string[]> = {
	FLUX: [
		'FLUX_BASE_CREATE',
		'FLUX_FILL_CREATE'
	],
	SDXL: [
		'SDXL_BASE_CREATE',
	],
}
